import { findActiveTab, type HostTabKey } from "@/lib/host-panel/host-tabs";

/**
 * Which kind of page a route is, for the `page_view` (INST §09). One table, one function: a route
 * that is not in it is `other`, never a guess.
 *
 * `page_path` is the route's **template** wherever a segment names a list, a piece or a record
 * (`/lista/[slug]`): the slug carries the couple's names and no personal data may reach the
 * dataLayer (D-02). The list is identified by its `id`, in `list_id`.
 */

export type AnalyticsPageType =
  | "home"
  | "suggestion"
  | "list_search"
  | "catalog"
  | "product"
  | "checkout"
  | "onboarding"
  | "host_panel"
  | "admin_kpi"
  | "admin_report"
  | "internal_new_list"
  | "other";

export type ResolvedPage = {
  pageType: AnalyticsPageType;
  /** The route's address with every dynamic segment replaced by its name. */
  pagePath: string;
  /** Only on the panel's tabs. */
  hostPanelTab?: HostTabKey;
  /** The page publishes a list (`list-context.ts`) that its `page_view` must wait for. */
  expectsList: boolean;
};

/** A route with no segment to hide and no list. */
function plain(pageType: AnalyticsPageType, pagePath: string): ResolvedPage {
  return { pageType, pagePath, expectsList: false };
}

export function resolvePage(rawPathname: string): ResolvedPage {
  // `/lista/x/` and `/lista/x` are one page.
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, "") : rawPathname;
  const segments = pathname.split("/").filter(Boolean);
  const [first, second, third, fourth] = segments;

  if (pathname === "/") return plain("home", "/");

  if (first === "listas-de-sugestao" && segments.length === 2) {
    return plain("suggestion", "/listas-de-sugestao/[slug]");
  }
  if (pathname === "/listas") return plain("list_search", "/listas");

  if (first === "lista" && segments.length === 2) {
    return { pageType: "catalog", pagePath: "/lista/[slug]", expectsList: true };
  }
  if (first === "lista" && third === "produto" && segments.length === 4 && fourth !== undefined) {
    return { pageType: "product", pagePath: "/lista/[slug]/produto/[itemId]", expectsList: true };
  }
  if (first === "produtos" && segments.length === 2) return plain("product", "/produtos/[id]");

  if (first === "checkout") return plain("checkout", pathname);

  if (pathname === "/onboarding" || pathname === "/onboarding/presentear") return plain("onboarding", pathname);

  if (first === "anfitriao") {
    if (second === "nova-lista") return plain("onboarding", pathname.replace(/\/listas-de-sugestao\/[^/]+$/, "/listas-de-sugestao/[slug]"));

    const tab = findActiveTab(pathname);
    if (tab !== undefined) return { pageType: "host_panel", pagePath: pathname, hostPanelTab: tab.key, expectsList: false };

    return plain("other", pathname);
  }

  if (first === "admin") {
    if (segments.length === 1) return plain("admin_kpi", "/admin");
    if (pathname === "/admin/listas") return plain("admin_report", "/admin/listas");
    if (second === "nova-lista") {
      const template = pathname
        .replace(/^\/admin\/nova-lista\/[^/]+/, "/admin/nova-lista/[listId]")
        .replace(/\/listas-de-sugestao\/[^/]+$/, "/listas-de-sugestao/[slug]");

      return plain("internal_new_list", template);
    }

    return plain("other", pathname);
  }

  return plain("other", pathname);
}
